import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Spinner,
  Alert,
  AlertIcon,
  Input,
  Select,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import SearchTable from "./SearchTable";
import * as IconButton from "../themes/IconButtonSet";
import TextSet from "../themes/TextSet";
import { faFileLines, faSearch } from "@fortawesome/free-solid-svg-icons";
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";

const API_URL = process.env.REACT_APP_API_URL;

const AssetSearch = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [assetTypes, setAssetTypes] = useState([]);
  const [assetCategories, setAssetCategories] = useState([]);
  const [acquisitionTypes, setAcquisitionTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [searchParams, setSearchParams] = useState({
    AssetCode: "",
    AssetName: "",
    BudgetYear: "",
    AssetCategoryID: "",
    AssetTypeID: "",
    AcquisitionType: "",
    DepartmentID: "",
    CostUnitID: "",
    Status: "",
  });

  useEffect(() => {
    fetchInitialData();
    fetchData();
  }, []);

  const fetchInitialData = async () => {
    try {
      // Fetch Asset Types
      const typesResponse = await fetch(`${API_URL}/getAssetTypesList`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const typesResult = await typesResponse.json();
      if (typesResult.result === "success" && typesResult.List) {
        setAssetTypes(typesResult.List);
      }

      // Fetch Asset Categories
      const categoriesResponse = await fetch(`${API_URL}/getAssetCategoriesList`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const categoriesResult = await categoriesResponse.json();
      if (categoriesResult.result === "success" && categoriesResult.List) {
        setAssetCategories(categoriesResult.List);
      }

      // Fetch Acquisition Types
      const acquisitionResponse = await fetch(`${API_URL}/getAcquisitionTypesList`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const acquisitionResult = await acquisitionResponse.json();
      if (acquisitionResult.result === "success" && acquisitionResult.List) {
        setAcquisitionTypes(acquisitionResult.List);
      }

      // Fetch Departments
      const departmentsResponse = await fetch(`${API_URL}/getOrganizationDepartmentsList`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const departmentsResult = await departmentsResponse.json();
      if (departmentsResult.result === "success" && departmentsResult.List) {
        setDepartments(departmentsResult.List);
      }

      // Fetch Cost Centers
      const costCentersResponse = await fetch(`${API_URL}/getCostCenterList`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const costCentersResult = await costCentersResponse.json();
      if (costCentersResult.result === "success" && costCentersResult.List) {
        setCostCenters(costCentersResult.List);
      }
    } catch (err) {
      setError("เกิดข้อผิดพลาดในการดึงข้อมูลเริ่มต้น");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/getAssetList`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(searchParams),
      });

      const result = await response.json();
      if (result.result === "success") {
        // Enrich data with names from master tables
        const enrichedData = result.AssetList.map((asset) => {
          const type = assetTypes.find((t) => t.AssetTypeID === asset.AssetTypeID);
          const category = assetCategories.find((c) => c.AssetCategoryID === asset.AssetCategoryID);
          const acquisition = acquisitionTypes.find((a) => a.AcquisitionTypeID === asset.AcquisitionType);
          const department = departments.find((d) => d.DepartmentID === asset.DepartmentID);
          const costCenter = costCenters.find((cc) => cc.CostCenterID === asset.CostUnitID);

          return {
            ...asset,
            TypeName: type ? type.TypeName : asset.AssetTypeID || "-",
            CategoryName: category ? category.CategoryName : asset.AssetCategoryID || "-",
            AcquisitionTypeName: acquisition ? acquisition.TypeName : asset.AcquisitionType || "-",
            DepartmentName: department ? department.DepartmentName : asset.DepartmentID || "-",
            CostCenterName: costCenter ? costCenter.CostCenterName : asset.CostUnitID || "-",
          };
        });
        setData(enrichedData || []);
      } else {
        setError("ไม่สามารถโหลดข้อมูลได้");
      }
    } catch (err) {
      setError("เกิดขั้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchParams((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    fetchData();
  };

  const handleRowClick = (row) => {
    const encodedID = encodeURIComponent(row.AssetID);
    navigate(`/asset/${encodedID}`);
    //navigate(`/asset/${row.AssetID}`);
  };

  const handleAdd = () => {
    navigate("/asset/new");
  };

  const columns = [
    { name: "รหัสครุภัณฑ์", selector: (row) => row.AssetCode, sortable: true, width:"250px" },
    { name: "ปีงบประมาณ", selector: (row) => row.BudgetYear || "-", sortable: true, width:"150px" },
    { name: "ชื่อครุภัณฑ์", selector: (row) => row.AssetName, sortable: true , width:"400px"},
    { name: "ประเภทการได้มา", selector: (row) => row.AcquisitionTypeName || "-", sortable: true},
    { name: "หน่วยต้นทุน", selector: (row) => row.CostCenterName || "-", sortable: true , width:"250px" },
    { name: "หน่วยงาน", selector: (row) => row.DepartmentName || "-", sortable: true , width:"250px"},
    //{ name: "ประเภทครุภัณฑ์", selector: (row) => row.CategoryName || "-", sortable: true },
    //{ name: "ประเภททรัพย์สิน", selector: (row) => row.TypeName || "-", sortable: true },
    //{ name: "ประเภทการได้มา", selector: (row) => row.AcquisitionTypeName || "-", sortable: true },
    { 
      name: "สถานะ", 
      selector: (row) => row.Status ? "ใช้งาน" : "ไม่ใช้งาน", 
      sortable: true , 
      width:"150px"
    },
  ];

  return (
    <Box pt={2} pl={5} pr={5}>        <StandardHeader
              title="รายการเบิกวัคซีน"
              bgColor="iBlue.600"
              icon={faFileLines}
            />
      <TextSet type="header" label="ข้อมูลครุภัณฑ์" icon="buildingWheat" />
      <Box textAlign="right" mb={2}>
        <IconButton.Add onClick={handleAdd} />
      </Box>
      <Box mb={5} p={5} borderWidth={1} borderRadius="md" boxShadow="sm">
        <Grid templateColumns="repeat(3, 1fr)" gap={6}>
          <GridItem>
            <FormControl>
              <FormLabel>รหัสครุภัณฑ์</FormLabel>
              <Input
                placeholder="รหัสครุภัณฑ์"
                name="AssetCode"
                value={searchParams.AssetCode}
                onChange={handleSearchChange}
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>ชื่อครุภัณฑ์</FormLabel>
              <Input
                placeholder="ชื่อครุภัณฑ์"
                name="AssetName"
                value={searchParams.AssetName}
                onChange={handleSearchChange}
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>ปีงบประมาณที่ได้รับ</FormLabel>
              <Input
                type="number"
                placeholder="ปีงบประมาณที่ได้รับ"
                name="BudgetYear"
                value={searchParams.BudgetYear}
                onChange={handleSearchChange}
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>ประเภทครุภัณฑ์</FormLabel>
              <Select
                placeholder="ประเภทครุภัณฑ์"
                name="AssetCategoryID"
                value={searchParams.AssetCategoryID}
                onChange={handleSearchChange}
              >
                {assetCategories.map((category) => (
                  <option key={category.AssetCategoryID} value={category.AssetCategoryID}>
                    {category.CategoryName}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>ประเภททรัพย์สิน</FormLabel>
              <Select
                placeholder="ประเภททรัพย์สิน"
                name="AssetTypeID"
                value={searchParams.AssetTypeID}
                onChange={handleSearchChange}
              >
                {assetTypes.map((type) => (
                  <option key={type.AssetTypeID} value={type.AssetTypeID}>
                    {type.TypeName}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>ประเภทการได้มา</FormLabel>
              <Select
                placeholder="ประเภทการได้มา"
                name="AcquisitionType"
                value={searchParams.AcquisitionType}
                onChange={handleSearchChange}
              >
                {acquisitionTypes.map((acquisition) => (
                  <option key={acquisition.AcquisitionTypeID} value={acquisition.AcquisitionTypeID}>
                    {acquisition.TypeName}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>หน่วยงาน</FormLabel>
              <Select
                placeholder="หน่วยงาน"
                name="DepartmentID"
                value={searchParams.DepartmentID}
                onChange={handleSearchChange}
              >
                {departments.map((department) => (
                  <option key={department.DepartmentID} value={department.DepartmentID}>
                    {department.DepartmentName}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>หน่วยต้นทุน</FormLabel>
              <Select
                placeholder="หน่วยต้นทุน"
                name="CostUnitID"
                value={searchParams.CostUnitID}
                onChange={handleSearchChange}
              >
                {costCenters.map((costCenter) => (
                  <option key={costCenter.CostCenterID} value={costCenter.CostCenterID}>
                    {costCenter.CostCenterName}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>สถานะ</FormLabel>
              <Select
                placeholder="สถานะ"
                name="Status"
                value={searchParams.Status}
                onChange={handleSearchChange}
              >
                <option value="1">ใช้งาน</option>
                <option value="0">ไม่ใช้งาน</option>
              </Select>
            </FormControl>
          </GridItem>
        </Grid>
        <IconButton.Search mt={5} onClick={handleSearch} />
      </Box>

      {loading ? (
        <Spinner size="xl" />
      ) : error ? (
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      ) : (
        <Box p={5} borderWidth={1} borderRadius="md" boxShadow="sm">
          <SearchTable
            columns={columns}
            data={data}
            onRowClicked={handleRowClick}
            pointerOnHover
            highlightOnHover
          />
        </Box>
      )}
    </Box>
  );
};

export default AssetSearch;